import i18next, { t } from "i18next";

export interface NumberRangeFilterValue {
  min?: number;
  max?: number;
  presetId?: string;
}

export interface NumberRangePreset {
  id: string;
  label: string;
  min?: number;
  max?: number;
}

export type NumberRangePresetSet = "price";

interface NumberRangePresetDefinition {
  id: string;
  min?: number;
  max?: number;
}

const getNumberPresetLabel = (id: string): string =>
  t(`admin.dataTable.filters.numberPresets.${id}`);

const NUMBER_RANGE_PRESET_DEFINITIONS: NumberRangePresetDefinition[] = [
  { id: "under5k", max: 4999 },
  { id: "5to15k", min: 5000, max: 15000 },
  { id: "15to30k", min: 15000, max: 30000 },
  { id: "over30k", min: 30001 }
];

export const getNumberPresetsForSet = (
  _set: NumberRangePresetSet
): NumberRangePreset[] =>
  NUMBER_RANGE_PRESET_DEFINITIONS.map(({ id, min, max }) => ({
    id,
    label: getNumberPresetLabel(id),
    min,
    max
  }));

export const isNumberRangeFilterValue = (
  value: unknown
): value is NumberRangeFilterValue => {
  if (!value || typeof value !== "object") return false;
  const v = value as Partial<NumberRangeFilterValue>;
  const minOk = v.min === undefined || typeof v.min === "number";
  const maxOk = v.max === undefined || typeof v.max === "number";
  return minOk && maxOk;
};

export const isNumberRangeEmpty = (value?: NumberRangeFilterValue): boolean => {
  return value?.min === undefined && value?.max === undefined;
};

export const isNumberInRange = (
  n: number | null | undefined,
  value: NumberRangeFilterValue | undefined
): boolean => {
  if (isNumberRangeEmpty(value)) return true;
  if (n == null || !Number.isFinite(n)) return false;
  if (value?.min !== undefined && n < value.min) return false;
  if (value?.max !== undefined && n > value.max) return false;
  return true;
};

const getNumberLocale = (): string =>
  i18next.language === "en" ? "en-US" : "fi-FI";

const formatAmount = (n: number): string =>
  n.toLocaleString(getNumberLocale(), { maximumFractionDigits: 0 });

export const formatNumberRangeLabel = (
  value: NumberRangeFilterValue | undefined,
  presets: NumberRangePreset[]
): string => {
  if (isNumberRangeEmpty(value)) return t("admin.dataTable.filters.allPrices");
  if (value?.presetId) {
    const preset = presets.find((p) => p.id === value.presetId);
    if (preset) return preset.label;
  }
  const min = value?.min !== undefined ? formatAmount(value.min) : "";
  const max = value?.max !== undefined ? formatAmount(value.max) : "";
  if (min && max) return `${min} – ${max}`;
  if (min) return `${min} →`;
  if (max) return `← ${max}`;
  return t("admin.dataTable.filters.allPrices");
};
